import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { AlertTriangle, Info, X } from 'lucide-react'
import { Card } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { PeriodSelector } from '@/components/analytics'
import {
  buildMarketingFactorAlerts,
  getLatestDateKey,
  MarketingFactorAnalysisCard,
  type MarketingPlanTotals
} from '@/components/marketing/PartnerMarketingFactorAnalysis'
import { supabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { formatMarketingTargetologistName } from '@/lib/marketingTargetologistName'
import {
  getEffectiveWeeklyPlan,
  getPartnerMarketingTargetKey,
  type PartnerMarketingWeeklyPlanRow
} from '@/lib/partnerMarketingWeeklyPlan'
import { formatCurrency, pluralizeRu } from '@/lib/utils'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useChartAxisTheme, useChartPalette } from '@/hooks/useThemeColors'
import type { MetricWeek } from '@/hooks/useMetricWeeks'
import { SURFACE_CARD_CLASS, SUBTLE_PANEL_CLASS, FILTER_CHIP_CLASS, INPUT_CLASS } from '@/components/marketing/marketingClasses'

type AccountTableSort =
  | 'spend_desc'
  | 'spend_asc'
  | 'conversions_desc'
  | 'conversions_asc'
  | 'cpa_asc'
  | 'cpa_desc'
  | 'account_asc'
  | 'account_desc'
  | 'service_asc'
  | 'service_desc'
  | 'campaigns_desc'
  | 'campaigns_asc'
type FilterOption = { value: string; label: string; searchText?: string; disabled?: boolean }
const DEFAULT_ACCOUNT_CURRENCY = 'RUB'
const VAT_MULTIPLIER = 1.22
const MARKETING_VAT_STORAGE_KEY = 'partner-marketing-include-vat'

interface ClickDailyRow {
  rowKey: string
  date: string
  service: string
  accountId: number
  account_name: string | null
  accountCurrency: string | null
  campaignId: number | null
  campaignName: string | null
  cost: number
  conversions: number
  cpa: number | null
}

interface DailyChartRow {
  date: string
  spend: number
  conversions: number
  cpa: number | null
  currency: string | null
}

interface AccountComparisonRow {
  key: string
  accountId: number
  service: string
  accountLabel: string
  serviceLabel: string
  spend: number
  spendByCurrency: Record<string, number>
  conversions: number
  cpa: number | null
  planSpend: number
  planLeads: number
  planCpa: number | null
  periodPlanSpend: number
  periodPlanLeads: number
  periodPlanCpa: number | null
  spendDeviation: number | null
  spendDeviationPercent: number | null
  leadsDeviation: number | null
  leadsDeviationPercent: number | null
  cpaDeviation: number | null
  cpaDeviationPercent: number | null
  leadsForecast: number | null
  leadsForecastDeviationPercent: number | null
  campaignCount: number
  rowCount: number
}

function normalizeCurrencyCode(value: string | null | undefined): string | null {
  if (!value) return null
  const normalized = value.trim().toUpperCase()
  return normalized || null
}

function getAccountCurrency(value: string | null | undefined): string {
  return normalizeCurrencyCode(value) || DEFAULT_ACCOUNT_CURRENCY
}

function getCurrencyCodes(spendByCurrency: Record<string, number>): string[] {
  return Object.keys(spendByCurrency).sort((a, b) => a.localeCompare(b, 'en'))
}

function getSingleCurrency(spendByCurrency: Record<string, number>): string | null {
  const currencies = getCurrencyCodes(spendByCurrency)
  return currencies.length === 1 ? currencies[0] : null
}

function getDisplayMoneyValue(value: number, includeVat: boolean): number {
  return includeVat ? value * VAT_MULTIPLIER : value
}

function MarketingInfoModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="max-h-[min(760px,calc(100vh-2rem))] w-full max-w-2xl overflow-y-auto rounded-2xl border border-border bg-card p-5 shadow-2xl sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby="marketing-info-title"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="marketing-info-title" className="text-xl font-semibold text-foreground">
              Важно для аналитики
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 text-muted-foreground transition hover:bg-muted hover:text-foreground"
            aria-label="Закрыть"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-5 space-y-5 text-sm leading-6 text-foreground">
          <section className="rounded-xl border border-amber-200 bg-amber-50/80 p-4">
            <h3 className="font-semibold text-amber-950">1. Как назвать рекламный кабинет в Click.ru</h3>
            <p className="mt-2 text-amber-950/90">
              Название кабинета нужно изменить непосредственно в{' '}
              <a
                href="https://click.ru/"
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline decoration-amber-700/50 underline-offset-2 hover:decoration-amber-900"
              >
                Click.ru
              </a>
              . Откройте список рекламных кабинетов, выберите нужный кабинет, измените название и сохраните его.
            </p>
            <p className="mt-3 font-medium text-amber-950">Используйте формат:</p>
            <p className="mt-1 rounded-lg bg-white/80 px-3 py-2 font-mono text-xs text-amber-950 sm:text-sm">
              UID_номер кабинета_Имя Фамилия_город
            </p>
            <p className="mt-3 text-amber-950/90">Например:</p>
            <p className="mt-1 rounded-lg bg-emerald-50 px-3 py-2 font-mono text-xs text-emerald-800 sm:text-sm">
              ✅ UID_1841166_Алексей Ветров_Апрелевка
            </p>
            <p className="mt-3 text-amber-950/90">
              Имя и фамилия таргетолога должны находиться{' '}
              <strong>между вторым и третьим нижними подчёркиваниями</strong>.{' '}
              Обязательно указывайте и имя, и фамилию: сначала имя, затем фамилию.
            </p>
            <p className="mt-3 text-amber-950/90">Примеры неправильных названий:</p>
            <div className="mt-1 space-y-1 rounded-lg bg-white/80 px-3 py-2 text-xs text-amber-950 sm:text-sm">
              <p>❌ UID_1841166_Ветров Алексей_Апрелевка — сначала указана фамилия</p>
              <p>❌ UID_1841166_Алексей_Апрелевка — отсутствует фамилия</p>
              <p>❌ UID_1841166_Алексей Ветров Апрелевка — пропущены разделители</p>
            </div>
            <p className="mt-3 font-medium text-amber-950">
              После изменения нажмите «Сохранить» и проверьте название ещё раз.
            </p>
            <p className="mt-2 text-amber-950/90">
              Если формат не соблюдён, таргетолог может определиться неправильно, а данные по кабинету могут не отобразиться.
            </p>
          </section>

          <section className="rounded-xl border border-border bg-muted/30 p-4">
            <h3 className="font-semibold text-foreground">2. Когда обновляются данные на портале</h3>
            <p className="mt-2">
              Загрузка начинается ежедневно примерно в <strong>04:30 МСК</strong> и обычно завершается до <strong>06:30 МСК</strong>.
            </p>
            <p className="mt-2">Загружаются данные за вчерашний день и обновляются показатели за последние три дня.</p>
            <p className="mt-2">Данные за сегодня появятся после следующей ночной загрузки.</p>
          </section>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background transition hover:opacity-90"
          >
            Понятно
          </button>
        </div>
      </div>
    </div>
  )
}

function formatMoneyValue(value: number, currency: string | null, includeVat = false): string {
  const displayValue = getDisplayMoneyValue(value, includeVat)

  if (currency) {
    return formatCurrency(displayValue, currency)
  }

  return formatNumber(displayValue)
}

function formatMoneyByCurrencies(spendByCurrency: Record<string, number>, includeVat = false): string {
  const currencies = getCurrencyCodes(spendByCurrency)

  if (currencies.length === 0) {
    return formatMoneyValue(0, null, includeVat)
  }

  if (currencies.length === 1) {
    const currency = currencies[0]
    return formatMoneyValue(spendByCurrency[currency] || 0, currency, includeVat)
  }

  return currencies
    .map((currency) => formatMoneyValue(spendByCurrency[currency] || 0, currency, includeVat))
    .join(' • ')
}

function getCurrencySymbol(currency: string | null): string {
  switch (currency) {
    case 'RUB':
      return '₽'
    case 'USD':
      return '$'
    case 'EUR':
      return '€'
    case 'KZT':
      return '₸'
    default:
      return currency || ''
  }
}

function formatCompactMoneyValue(value: number, currency: string | null, includeVat = false): string {
  const displayValue = getDisplayMoneyValue(value, includeVat)
  const roundedThousands = Math.round(displayValue / 1000)

  if (Math.abs(displayValue) < 1000 || !currency) {
    return formatMoneyValue(Math.round(value), currency, includeVat)
  }

  return `${roundedThousands.toLocaleString('ru-RU')}к ${getCurrencySymbol(currency)}`.trim()
}

function formatCompactMoneyByCurrencies(spendByCurrency: Record<string, number>, includeVat = false): string {
  const currencies = getCurrencyCodes(spendByCurrency)

  if (currencies.length === 0) {
    return formatCompactMoneyValue(0, null, includeVat)
  }

  if (currencies.length === 1) {
    const currency = currencies[0]
    return formatCompactMoneyValue(spendByCurrency[currency] || 0, currency, includeVat)
  }

  return currencies
    .map((currency) => formatCompactMoneyValue(spendByCurrency[currency] || 0, currency, includeVat))
    .join(' • ')
}

function formatSelectedItemsLabel(
  selectedValues: string[] | null,
  options: FilterOption[],
  allLabel: string,
  noun: string
): string {
  if (selectedValues === null || selectedValues.length >= options.length) return allLabel
  if (selectedValues.length === 0) return 'Ничего не выбрано'
  if (selectedValues.length === 1) {
    return options.find((option) => option.value === selectedValues[0])?.label || `1 ${noun}`
  }

  return `Выбрано ${noun}: ${selectedValues.length}`
}

function sortFilterOptions(options: FilterOption[]): FilterOption[] {
  return [...options].sort((a, b) => {
    if (Boolean(a.disabled) !== Boolean(b.disabled)) {
      return a.disabled ? 1 : -1
    }

    return a.label.localeCompare(b.label, 'ru')
  })
}

function MultiSelectFilter({
  label,
  selectedValues,
  onChange,
  options,
  allLabel,
  noun,
  searchPlaceholder
}: {
  label: string
  selectedValues: string[] | null
  onChange: (values: string[] | null) => void
  options: FilterOption[]
  allLabel: string
  noun: string
  searchPlaceholder: string
}) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const normalizedQuery = searchQuery.trim().toLowerCase()
  const enabledOptions = options.filter((option) => !option.disabled)
  const enabledValues = enabledOptions.map((option) => option.value)
  const allSelected = selectedValues === null || selectedValues.length >= enabledOptions.length
  const selectedValueSet = new Set(allSelected ? enabledValues : selectedValues)
  const filteredOptions = normalizedQuery
    ? options.filter((option) => `${option.label} ${option.searchText || ''}`.toLowerCase().includes(normalizedQuery))
    : options

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const normalizeSelection = (values: string[]) => {
    const nextValues = Array.from(new Set(values)).filter((value) => enabledOptions.some((option) => option.value === value))
    if (nextValues.length >= enabledOptions.length) {
      onChange(null)
      return
    }

    onChange(nextValues)
  }

  const toggleValue = (value: string) => {
    const option = options.find((item) => item.value === value)
    if (option?.disabled) return

    const currentValues = allSelected ? enabledValues : (selectedValues || [])

    if (currentValues.includes(value)) {
      normalizeSelection(currentValues.filter((item) => item !== value))
      return
    }

    normalizeSelection([...currentValues, value])
  }

  const selectedLabel = formatSelectedItemsLabel(selectedValues, options, allLabel, noun)

  return (
    <div ref={containerRef} className="relative space-y-1.5">
      <span className="text-sm font-medium">{label}</span>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={`flex items-center justify-between text-left ${INPUT_CLASS}`}
      >
        <span className="truncate pr-3">{selectedLabel}</span>
        <span className={`text-xs text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}>⌄</span>
      </button>

      {open ? (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-border bg-card p-2 shadow-card">
          <div className="mb-2 flex items-center gap-2">
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className={`${INPUT_CLASS} py-2 text-xs`}
            />
            <button
              type="button"
              onClick={() => {
                setSearchQuery('')
                onChange([])
              }}
              className="shrink-0 rounded-lg border border-border px-2.5 py-2 text-xs font-medium text-muted-foreground transition hover:bg-muted"
            >
              Сбросить
            </button>
          </div>

          <div className="max-h-80 overflow-y-auto">
          <label className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-muted">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => onChange(allSelected ? [] : null)}
              className="h-4 w-4 rounded border-border"
            />
            <span>{allLabel}</span>
          </label>

          <div className="my-1 border-t border-border/60" />

          {filteredOptions.map((option) => (
            <label
              key={option.value}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm ${
                option.disabled
                  ? 'cursor-not-allowed opacity-45'
                  : 'cursor-pointer hover:bg-muted'
              }`}
            >
              <input
                type="checkbox"
                checked={selectedValueSet.has(option.value)}
                disabled={option.disabled}
                onChange={() => toggleValue(option.value)}
                className="h-4 w-4 rounded border-border"
              />
              <span className="min-w-0 truncate">{option.label}</span>
            </label>
          ))}
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-foreground">Ничего не найдено.</div>
          ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function KpiCard({
  label,
  value,
  secondaryValue,
  comparisonLabel,
  cornerValue,
  cornerTone = 'default',
  progressValue,
  progressTone = 'default',
  progressMarkerValue,
  planToDateLabel,
  planToDateValue,
  footerTiles,
  reserveProgressSpace = false
}: {
  label: string
  value: string
  secondaryValue?: string
  comparisonLabel?: string
  cornerValue?: string | null
  cornerTone?: 'default' | 'positive' | 'negative' | 'warning'
  progressValue?: string | null
  progressTone?: 'default' | 'positive' | 'negative' | 'warning'
  progressMarkerValue?: string | null
  planToDateLabel?: string
  planToDateValue?: string
  reserveProgressSpace?: boolean
  footerTiles?: Array<{
    label: string
    value: string
    hint?: string
    tone?: 'default' | 'positive' | 'negative' | 'warning'
  }>
}) {
  const getToneClasses = (tone: 'default' | 'positive' | 'negative' | 'warning' = 'default') => {
    switch (tone) {
      case 'positive':
        return {
          text: 'text-emerald-700',
          panel: 'border-emerald-200 bg-emerald-50',
          bar: 'bg-emerald-500'
        }
      case 'negative':
        return {
          text: 'text-red-600',
          panel: 'border-red-200 bg-red-50',
          bar: 'bg-red-500'
        }
      case 'warning':
        return {
          text: 'text-amber-700',
          panel: 'border-amber-200 bg-amber-50',
          bar: 'bg-amber-500'
        }
      default:
        return {
          text: 'text-muted-foreground',
          panel: 'border-border bg-muted',
          badge: 'border-border bg-muted text-muted-foreground',
          bar: 'bg-amber-500'
        }
    }
  }

  const normalizedProgress = progressValue ? Number.parseFloat(progressValue) : null
  const progressWidth = normalizedProgress === null || Number.isNaN(normalizedProgress)
    ? 0
    : Math.max(0, Math.min(100, normalizedProgress))
  const normalizedProgressMarker = progressMarkerValue ? Number.parseFloat(progressMarkerValue) : null
  const progressMarkerWidth = normalizedProgressMarker === null || Number.isNaN(normalizedProgressMarker)
    ? progressWidth
    : Math.max(0, Math.min(100, normalizedProgressMarker))
  const cornerStyle = getToneClasses(cornerTone)
  const progressStyle = getToneClasses(progressTone)
  const hasProgressSection = Boolean(progressValue) || reserveProgressSpace

  return (
    <Card className={`${SURFACE_CARD_CLASS} h-full overflow-hidden`}>
      <div className="flex h-full flex-col gap-4 p-4">
        <div className="h-1 w-14 rounded-full bg-amber-500" />
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
          </div>
          {cornerValue ? (
            <div className={`rounded-full border px-3 py-1 text-lg font-semibold leading-none ${cornerStyle.panel} ${cornerStyle.text}`}>
              {cornerValue}
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-[40px] font-semibold leading-none tracking-tight text-foreground">{value}</span>
            {secondaryValue ? <span className="text-2xl font-medium text-muted-foreground/70">/ {secondaryValue}</span> : null}
          </div>
        </div>

        {progressValue ? (
          <div className="space-y-3 border-t border-border/60 pt-3">
            <div className="relative h-5">
              <div className="absolute inset-x-0 top-1 h-3 overflow-hidden rounded-full bg-muted ring-1 ring-border">
                <div
                  className={`h-full rounded-full transition-all ${progressStyle.bar}`}
                  style={{ width: `${progressWidth}%` }}
                />
              </div>
              <div
                className="absolute top-0 z-10 h-5 w-[2px] rounded-full bg-foreground shadow-sm"
                style={{
                  left: `${progressMarkerWidth}%`,
                  transform: 'translateX(-50%)'
                }}
              />
            </div>
            {planToDateLabel && planToDateValue ? (
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-muted-foreground">{planToDateLabel}</span>
                <span className="font-semibold text-foreground">{planToDateValue}</span>
              </div>
            ) : null}
          </div>
        ) : reserveProgressSpace ? (
          <div className="border-t border-border/60 pt-3">
            <div className="h-[52px]" />
          </div>
        ) : null}

        {footerTiles && footerTiles.length > 0 ? (
          <div className={`${hasProgressSection ? 'mt-auto ' : ''}grid gap-3 ${footerTiles.length > 1 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
            {footerTiles.map((tile) => {
              const tileStyle = getToneClasses(tile.tone)
              return (
                <div key={tile.label} className={`rounded-2xl border px-3.5 py-3 ${tileStyle.panel}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    {tile.label}
                  </div>
                  <div className={`mt-1 text-xl font-semibold ${tileStyle.text}`}>
                    {tile.value}
                  </div>
                  {tile.hint ? <div className="mt-1 text-xs text-muted-foreground">{tile.hint}</div> : null}
                </div>
              )
            })}
          </div>
        ) : null}
      </div>
    </Card>
  )
}

function getAccountDisplayLabel(row: ClickDailyRow): string {
  return formatMarketingTargetologistName(row.account_name) || `Таргетолог ${row.accountId}`
}

function getCampaignKey(row: ClickDailyRow): string {
  return row.campaignId !== null ? String(row.campaignId) : `${row.service}:${row.campaignName || 'Без названия'}`
}

function getCampaignDisplayLabel(row: ClickDailyRow): string {
  return row.campaignName || (row.campaignId !== null ? `Кампания ${row.campaignId}` : 'Без названия')
}

function computePeriodWeeks(period: {
  period_id: number
  period_start: string
  period_end: string
}): MetricWeek[] {
  const result: MetricWeek[] = []
  const startDate = new Date(`${period.period_start}T00:00:00`)
  const endDate = new Date(`${period.period_end}T00:00:00`)
  let cursor = new Date(startDate)
  let weekNumber = 1

  const fmt = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  while (cursor <= endDate) {
    const weekStart = new Date(cursor)
    const weekEnd = new Date(cursor)
    weekEnd.setDate(weekEnd.getDate() + 6)
    if (weekEnd > endDate) {
      weekEnd.setTime(endDate.getTime())
    }

    result.push({
      week_id: -weekNumber,
      period_id: period.period_id,
      week_number_in_period: weekNumber,
      week_start: fmt(weekStart),
      week_end: fmt(weekEnd),
      week_len_days: Math.floor((weekEnd.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
    })

    cursor = new Date(weekEnd)
    cursor.setDate(cursor.getDate() + 1)
    weekNumber += 1
  }

  return result
}

function SortArrows({
  activeSort,
  ascValue,
  descValue,
  onChange
}: {
  activeSort: string
  ascValue: string
  descValue: string
  onChange: (value: string) => void
}) {
  const isAscActive = activeSort === ascValue
  const isDescActive = activeSort === descValue

  return (
    <span className="ml-1.5 inline-flex w-3.5 flex-col items-center justify-center leading-none align-middle">
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          onChange(ascValue)
        }}
        className={`h-2.5 text-[9px] leading-none transition ${isAscActive ? 'text-amber-700' : 'text-muted-foreground/50 hover:text-foreground'}`}
        aria-label="Сортировать по возрастанию"
      >
        ▲
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          onChange(descValue)
        }}
        className={`h-2.5 text-[9px] leading-none transition ${isDescActive ? 'text-amber-700' : 'text-muted-foreground/50 hover:text-foreground'}`}
        aria-label="Сортировать по убыванию"
      >
        ▼
      </button>
    </span>
  )
}

function SortableHeader({
  label,
  activeSort,
  ascValue,
  descValue,
  onChange,
  align = 'left'
}: {
  label: string
  activeSort: string
  ascValue: string
  descValue: string
  onChange: (value: string) => void
  align?: 'left' | 'right'
}) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap ${align === 'right' ? 'justify-end' : ''}`}>
      <span>{label}</span>
      <SortArrows activeSort={activeSort} ascValue={ascValue} descValue={descValue} onChange={onChange} />
    </span>
  )
}

function AccountComparisonTable({
  rows,
  sort,
  includeVat = false,
  onSortChange,
  planHint,
  partnerNumber,
  periodFilterValue
}: {
  rows: AccountComparisonRow[]
  sort: AccountTableSort
  includeVat?: boolean
  onSortChange: (value: AccountTableSort) => void
  planHint?: string | null
  partnerNumber: number
  periodFilterValue: string
}) {
  const getToneClass = (tone: 'default' | 'positive' | 'negative') => {
    switch (tone) {
      case 'positive':
        return 'text-emerald-700'
      case 'negative':
        return 'text-red-600'
      default:
        return 'text-foreground'
    }
  }

  return (
    <Card className={SURFACE_CARD_CLASS}>
      <div className="space-y-4">
        <div>
          <h2 className="text-base font-semibold">Сравнение таргетологов</h2>
          {planHint ? <p className="mt-1 text-xs text-muted-foreground">{planHint}</p> : null}
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">Нет данных по таргетологам.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] table-fixed text-sm">
              <colgroup>
                <col className="w-[17%]" />
                <col className="w-[7%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[10%]" />
                <col className="w-[11%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-border/60 text-left text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="pb-2 pr-3" rowSpan={2}>Таргетолог</th>
                  <th className="pb-2 pr-1" rowSpan={2}>Канал</th>
                  <th className="pb-2 text-center" colSpan={2}>Расход</th>
                  <th className="pb-2 text-center" colSpan={3}>Лиды</th>
                  <th className="pb-2 text-center" colSpan={2}>CPL</th>
                  <th className="pb-2 pl-2 text-center tracking-[0.1em]" rowSpan={2}>Детализация</th>
                </tr>
                <tr className="border-b border-border/60 text-left text-muted-foreground">
                  <th className="pb-2 text-center font-medium">
                    <SortableHeader label="Факт / план" activeSort={sort} ascValue="spend_asc" descValue="spend_desc" onChange={onSortChange} />
                  </th>
                  <th className="pb-2 text-center font-medium">
                    <div className="inline-flex flex-col items-center leading-[1.1]">
                      <span>Отклонение</span>
                      <span className="mt-[3px] text-[9px] font-normal normal-case tracking-normal text-muted-foreground/70">от плана на вчера</span>
                    </div>
                  </th>
                  <th className="pb-2 text-center font-medium">
                    <SortableHeader label="Факт / план" activeSort={sort} ascValue="conversions_asc" descValue="conversions_desc" onChange={onSortChange} />
                  </th>
                  <th className="pb-2 text-center font-medium">
                    <div className="inline-flex flex-col items-center leading-[1.1]">
                      <span>Отклонение</span>
                      <span className="mt-[3px] text-[9px] font-normal normal-case tracking-normal text-muted-foreground/70">от плана на вчера</span>
                    </div>
                  </th>
                  <th className="pb-2 text-center font-medium">Прогноз</th>
                  <th className="pb-2 text-center font-medium">
                    <SortableHeader label="Факт / план" activeSort={sort} ascValue="cpa_asc" descValue="cpa_desc" onChange={onSortChange} />
                  </th>
                  <th className="pb-2 text-center font-medium">Отклонение</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const rowCurrency = getSingleCurrency(row.spendByCurrency)
                  const periodSpendPlanValue = row.periodPlanSpend > 0
                    ? formatMoneyValue(row.periodPlanSpend, rowCurrency, includeVat)
                    : '—'
                  const yesterdaySpendPlanValue = row.planSpend > 0
                    ? formatMoneyValue(row.planSpend, rowCurrency, includeVat)
                    : '—'
                  const periodLeadsPlanValue = row.periodPlanLeads > 0
                    ? formatNumber(row.periodPlanLeads)
                    : '—'
                  const yesterdayLeadsPlanValue = row.planLeads > 0
                    ? formatNumber(row.planLeads)
                    : '—'
                  const cplTone: 'default' | 'positive' | 'negative' =
                    row.cpaDeviationPercent === null
                      ? 'default'
                      : row.cpaDeviationPercent > 0
                        ? 'negative'
                        : 'positive'
                  const leadsTone: 'default' | 'positive' | 'negative' =
                    row.leadsDeviation === null
                      ? 'default'
                      : row.leadsDeviation >= 0
                        ? 'positive'
                        : 'negative'
                  const forecastTone: 'default' | 'positive' | 'negative' =
                    row.leadsForecastDeviationPercent === null
                      ? 'default'
                      : row.leadsForecastDeviationPercent >= 0
                        ? 'positive'
                        : 'negative'
                  const detailsParams = new URLSearchParams()
                  detailsParams.set('service', row.service)
                  detailsParams.set('accountId', String(row.accountId))
                  if (periodFilterValue && periodFilterValue !== 'last_3_days') {
                    detailsParams.set('periodFilter', periodFilterValue)
                  }

                  return (
                    <tr key={row.key} className="border-b border-border/60 align-middle">
                      <td className="py-3 pr-3 font-medium">{row.accountLabel}</td>
                      <td className="py-3 pr-1 text-xs">{row.serviceLabel}</td>

                      <td className="py-3 text-center">
                        <div className="text-base font-semibold text-foreground">
                          {formatMoneyByCurrencies(row.spendByCurrency, includeVat)}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{periodSpendPlanValue}</div>
                      </td>
                      <td className="py-3 text-center">
                        <div className={getToneClass(row.spendDeviation === null ? 'default' : 'negative')}>
                          {row.spendDeviation === null
                            ? '—'
                            : `${row.spendDeviation > 0 ? '+' : '−'}${formatMoneyValue(Math.abs(row.spendDeviation), rowCurrency, includeVat)}`}
                        </div>
                        {row.spendDeviationPercent !== null ? (
                          <div className={`mt-0.5 text-xs ${getToneClass('negative')}`}>
                            {formatPercent(row.spendDeviationPercent)}
                          </div>
                        ) : null}
                        <div className="mt-1 text-[10px] leading-4 text-muted-foreground">план: {yesterdaySpendPlanValue}</div>
                      </td>

                      <td className="py-3 text-center">
                        <div className="text-base font-semibold text-foreground">{formatNumber(row.conversions)}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{periodLeadsPlanValue}</div>
                      </td>
                      <td className="py-3 text-center">
                        <div className={getToneClass(leadsTone)}>
                          {row.leadsDeviation === null
                            ? '—'
                            : `${row.leadsDeviation > 0 ? '+' : row.leadsDeviation < 0 ? '−' : ''}${formatNumber(Math.abs(row.leadsDeviation))}`}
                        </div>
                        {row.leadsDeviationPercent !== null ? (
                          <div className={`mt-0.5 text-xs ${getToneClass(leadsTone)}`}>
                            {formatPercent(row.leadsDeviationPercent)}
                          </div>
                        ) : null}
                        <div className="mt-1 text-[10px] leading-4 text-muted-foreground">план: {yesterdayLeadsPlanValue}</div>
                      </td>
                      <td className="py-3 text-center">
                        <div className={`text-base font-semibold ${getToneClass(forecastTone)}`}>
                          {row.leadsForecast === null ? '—' : formatInteger(row.leadsForecast)}
                        </div>
                        {row.leadsForecastDeviationPercent !== null ? (
                          <div className={`mt-0.5 text-xs ${getToneClass(forecastTone)}`}>
                            {formatPercent(row.leadsForecastDeviationPercent)}
                          </div>
                        ) : null}
                      </td>

                      <td className="py-3 text-center">
                        <div className="text-base font-semibold text-foreground">{formatCpa(row.cpa, rowCurrency, includeVat)}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {formatCpa(row.periodPlanCpa, rowCurrency, includeVat)}
                        </div>
                      </td>
                      <td className="py-3 text-center">
                        <div className={getToneClass(cplTone)}>
                          {row.cpaDeviation === null
                            ? '—'
                            : `${row.cpaDeviation > 0 ? '+' : '−'}${formatMoneyValue(Math.abs(row.cpaDeviation), rowCurrency, includeVat)}`}
                        </div>
                        {row.cpaDeviationPercent !== null ? (
                          <div className={`mt-0.5 text-xs ${getToneClass(cplTone)}`}>
                            {formatPercent(row.cpaDeviationPercent)}
                          </div>
                        ) : null}
                      </td>

                      <td className="py-3 text-center">
                        <Link
                          to={`/partners/${partnerNumber}/marketing/daily?${detailsParams.toString()}`}
                          className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:bg-muted"
                        >
                          Подробнее
                        </Link>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  )
}

function MarketingTooltip({ active, payload, label, includeVat = false }: any) {
  if (!active || !payload?.length) return null

  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-card">
      <div className="mb-1 text-sm font-medium">{label}</div>
      {payload.map((item: any) => (
        <div key={item.dataKey} className="text-xs" style={{ color: item.color }}>
          {item.name}:{' '}
          {item.dataKey === 'conversions'
            ? formatNumber(Number(item.value))
            : item.dataKey === 'cpa'
              ? formatCpa(Number(item.value), item.payload?.currency || null, includeVat)
              : formatSpend(Number(item.value), item.payload?.currency || null, includeVat)}
        </div>
      ))}
    </div>
  )
}

function formatNumber(value: number): string {
  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  })
}

function formatInteger(value: number): string {
  return Math.round(value).toLocaleString('ru-RU')
}

function formatDayCount(value: number): string {
  return `${formatNumber(value)} ${pluralizeRu(value, 'день', 'дня', 'дней')}`
}

function formatSpend(value: number, currency: string | null, includeVat = false): string {
  return formatMoneyValue(value, currency, includeVat)
}

function formatCpa(value: number | null, currency: string | null, includeVat = false): string {
  if (value === null || !Number.isFinite(value)) return '—'
  return formatMoneyValue(value, currency, includeVat)
}

function formatPercent(value: number): string {
  const rounded = Math.round((value + Number.EPSILON) * 10) / 10
  return `${rounded > 0 ? '+' : ''}${rounded.toLocaleString('ru-RU', {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1
  })}%`
}

function formatPercentPlain(value: number): string {
  const rounded = Math.round((value + Number.EPSILON) * 10) / 10
  return `${rounded.toLocaleString('ru-RU', {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1
  })}%`
}

function scaleSpendByCurrencies(spendByCurrency: Record<string, number>, ratio: number): Record<string, number> {
  return Object.fromEntries(
    Object.entries(spendByCurrency).map(([currency, value]) => [currency, value * ratio])
  )
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function getInclusiveDays(start: string, end: string): number {
  const startDate = new Date(`${start}T00:00:00`)
  const endDate = new Date(`${end}T00:00:00`)
  return Math.max(1, Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1)
}

function getOverlapDays(
  rangeStart: string,
  rangeEnd: string,
  segmentStart: string,
  segmentEnd: string
): number {
  const overlapStart = rangeStart > segmentStart ? rangeStart : segmentStart
  const overlapEnd = rangeEnd < segmentEnd ? rangeEnd : segmentEnd

  if (overlapStart > overlapEnd) {
    return 0
  }

  return getInclusiveDays(overlapStart, overlapEnd)
}

function buildAccountComparisonRows(rows: ClickDailyRow[]): AccountComparisonRow[] {
  const map = new Map<string, AccountComparisonRow & { campaignKeys: Set<string>; dateKeys: Set<string> }>()

  for (const row of rows) {
    const key = getPartnerMarketingTargetKey(row.service, row.accountId)
    const current = map.get(key) || {
      key,
      accountId: row.accountId,
      service: row.service,
      accountLabel: getAccountDisplayLabel(row),
      serviceLabel: row.service,
      spend: 0,
      spendByCurrency: {},
      conversions: 0,
      cpa: null,
      planSpend: 0,
      planLeads: 0,
      planCpa: null,
      periodPlanSpend: 0,
      periodPlanLeads: 0,
      periodPlanCpa: null,
      spendDeviation: null,
      spendDeviationPercent: null,
      leadsDeviation: null,
      leadsDeviationPercent: null,
      cpaDeviation: null,
      cpaDeviationPercent: null,
      leadsForecast: null,
      leadsForecastDeviationPercent: null,
      campaignCount: 0,
      rowCount: 0,
      campaignKeys: new Set<string>(),
      dateKeys: new Set<string>()
    }

    current.spend += row.cost
    const currencyKey = getAccountCurrency(row.accountCurrency)
    current.spendByCurrency[currencyKey] = (current.spendByCurrency[currencyKey] || 0) + row.cost
    current.conversions += row.conversions
    current.cpa = current.conversions > 0 ? current.spend / current.conversions : null
    current.campaignKeys.add(getCampaignKey(row))
    current.dateKeys.add(row.date)
    current.campaignCount = current.campaignKeys.size
    current.rowCount = current.dateKeys.size

    map.set(key, current)
  }

  return Array.from(map.values()).map(({ campaignKeys, dateKeys, ...item }) => item)
}

export function PartnerMarketingPage() {
  // Recharts кладёт цвет в SVG-атрибуты, где CSS-переменная не разрешается,
  // поэтому цвета сетки, осей и серий вычисляются из темы в JS.
  const chartTheme = useChartAxisTheme()
  /*
   * ⚠️ Цвета СЕРИЙ намеренно НЕ следуют теме (правило useChartAxisTheme):
   * они несут смысл, а не оформление. Взяты оттенки 500 — единственные
   * самозеркальные в лестнице, поэтому линия выглядит одинаково в обеих
   * темах и читается и на белой карточке, и на тёмной. До этого CPL брал
   * `--mkt-highlight`, который зеркалился в #6D00AC: контраст 1,53:1.
   */
  const chartSeries = useChartPalette()

  const { partnerNumber, studioId } = useParams<{ partnerNumber: string; studioId?: string }>()
  const parsedPartnerNumber = Number(partnerNumber)
  const parsedStudioId = studioId ? Number(studioId) : undefined

  const periods = useStudioPeriods()
  const {
    selectedTimeFilter,
    setSelectedTimeFilter,
    timeFilterOptions,
    selectedRange
  } = useAnalyticsTimeFilter(periods.currentPeriod, {
    includeLastThreeDaysOption: true,
    isCurrentPeriod: periods.isCurrentPeriod
  })

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rows, setRows] = useState<ClickDailyRow[]>([])
  const [planWeeks, setPlanWeeks] = useState<MetricWeek[]>([])
  const [planRowsData, setPlanRowsData] = useState<PartnerMarketingWeeklyPlanRow[]>([])
  const [selectedServices, setSelectedServices] = useState<string[] | null>(null)
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[] | null>(null)
  const [selectedCampaignKeys, setSelectedCampaignKeys] = useState<string[] | null>(null)
  const [includeVat, setIncludeVat] = useState(() => {
    if (typeof window === 'undefined') return false

    try {
      return localStorage.getItem(MARKETING_VAT_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })
  const [accountTableSort, setAccountTableSort] = useState<AccountTableSort>('spend_desc')
  const [marketingInfoOpen, setMarketingInfoOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      localStorage.setItem(MARKETING_VAT_STORAGE_KEY, includeVat ? '1' : '0')
    } catch {
      // Ignore storage errors and keep the current in-memory toggle state.
    }
  }, [includeVat])

  useEffect(() => {
    if (!Number.isFinite(parsedPartnerNumber) || !selectedRange || !periods.currentPeriod) {
      setPlanWeeks([])
      setPlanRowsData([])
      return
    }

    let cancelled = false

    const loadPlanContext = async () => {
      try {
        let overlappingWeeks: MetricWeek[] = []

        if (selectedRange.mode === 'last_3_days') {
          const { data: rawWeeks, error: weeksError } = await withAuthRecovery(() =>
            supabase
              .from('metric_weeks')
              .select('*')
              .lte('week_start', selectedRange.end)
              .gte('week_end', selectedRange.start)
              .order('week_start', { ascending: true })
          )

          if (weeksError) throw weeksError

          overlappingWeeks = ((rawWeeks as MetricWeek[] | null) || [])
            .filter((week) => week.week_start <= selectedRange.end && week.week_end >= selectedRange.start)
            .sort((a, b) => a.week_start.localeCompare(b.week_start, 'en') || a.week_end.localeCompare(b.week_end, 'en'))
        } else {
          const { data: rawWeeks, error: weeksError } = await withAuthRecovery(() =>
            supabase
              .from('metric_weeks')
              .select('*')
              .eq('period_id', periods.currentPeriod!.period_id)
              .order('week_number_in_period', { ascending: true })
          )

          if (weeksError) throw weeksError

          const periodWeeks = (rawWeeks as MetricWeek[] | null) || []
          overlappingWeeks = periodWeeks.length > 0 ? periodWeeks : computePeriodWeeks(periods.currentPeriod!)
        }

        if (!cancelled) {
          setPlanWeeks(overlappingWeeks)
        }

        const weekIds = overlappingWeeks.map((week) => week.week_id).filter((weekId) => weekId > 0)
        if (weekIds.length === 0) {
          if (!cancelled) setPlanRowsData([])
          return
        }

        const { data: rawPlans, error: plansError } = await withAuthRecovery(() =>
          (supabase as any)
            .from('click_weekly_plan')
            .select('partner_number, week_id, service, account_id, planning_status, leads_plan, spend_plan, updated_at, updated_by_user_id')
            .eq('partner_number', parsedPartnerNumber)
            .in('week_id', weekIds)
        )

        if (plansError) throw plansError
        if (!cancelled) {
          setPlanRowsData(((rawPlans || []) as PartnerMarketingWeeklyPlanRow[]))
        }
      } catch (plansError) {
        console.error('❌ PartnerMarketingPage plan context load failed:', plansError)
        if (!cancelled) {
          setPlanWeeks([])
          setPlanRowsData([])
        }
      }
    }

    loadPlanContext()

    return () => {
      cancelled = true
    }
  }, [parsedPartnerNumber, periods.currentPeriod, selectedRange])

  useEffect(() => {
    if (!Number.isFinite(parsedPartnerNumber)) {
      setLoading(false)
      setError('Не удалось определить франчайзи')
      setRows([])
      return
    }

    if (periods.loading) return

    if (!periods.currentPeriod || !selectedRange) {
      setLoading(false)
      setRows([])
      setError(periods.error ? 'Не удалось загрузить период аналитики' : null)
      return
    }

    const loadData = async () => {
      setLoading(true)
      setError(null)

      try {
        const result = await withAuthRecovery(() =>
          (supabase as any)
            .rpc('get_partner_click_stats', {
              p_partner_number: parsedPartnerNumber,
              p_date_from: selectedRange.start,
              p_date_to: selectedRange.end
            })
        )

        if (result.error) throw result.error

        const mappedRows = ((result.data || []) as any[]).map((item, index) => ({
          rowKey: [
            item.metric_date,
            item.account_id,
            item.campaign_id ?? item.campaign_name ?? 'no-campaign',
            item.cost ?? 0,
            item.conversions ?? 0,
            index
          ].join(':'),
          date: item.metric_date,
          service: item.service || 'Без сервиса',
          accountId: Number(item.account_id),
          account_name: item.account_name || null,
          accountCurrency: getAccountCurrency(item.account_currency),
          campaignId: item.campaign_id === null ? null : Number(item.campaign_id),
          campaignName: item.campaign_name || null,
          cost: Number(item.cost || 0),
          conversions: Number(item.conversions || 0),
          cpa: item.cpa === null ? null : Number(item.cpa)
        })) as ClickDailyRow[]

        setRows(mappedRows)
      } catch (err) {
        console.error('❌ PartnerMarketingPage load failed:', err)
        setError('Не удалось загрузить маркетинговые данные')
        setRows([])
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [parsedPartnerNumber, periods.currentPeriod, periods.error, periods.loading, selectedRange])

  const rowsForServiceOptions = useMemo(() => {
    return rows.filter((row) => {
      if (selectedAccountIds !== null && selectedAccountIds.length > 0 && !selectedAccountIds.includes(String(row.accountId))) return false
      if (selectedCampaignKeys !== null && selectedCampaignKeys.length > 0 && !selectedCampaignKeys.includes(getCampaignKey(row))) return false
      return true
    })
  }, [rows, selectedAccountIds, selectedCampaignKeys])

  const rowsForAccountOptions = useMemo(() => {
    return rows.filter((row) => {
      if (selectedServices !== null && selectedServices.length > 0 && !selectedServices.includes(row.service)) return false
      if (selectedCampaignKeys !== null && selectedCampaignKeys.length > 0 && !selectedCampaignKeys.includes(getCampaignKey(row))) return false
      return true
    })
  }, [rows, selectedServices, selectedCampaignKeys])

  const rowsForCampaignOptions = useMemo(() => {
    return rows.filter((row) => {
      if (selectedServices !== null && selectedServices.length > 0 && !selectedServices.includes(row.service)) return false
      if (selectedAccountIds !== null && selectedAccountIds.length > 0 && !selectedAccountIds.includes(String(row.accountId))) return false
      return true
    })
  }, [rows, selectedServices, selectedAccountIds])

  const serviceOptions = useMemo(() => {
    const allValues = Array.from(new Set(rows.map((row) => row.service))).sort((a, b) => a.localeCompare(b, 'ru'))
    const enabledValues = new Set(rowsForServiceOptions.map((row) => row.service))
    return sortFilterOptions(
      allValues.map((value) => ({ value, label: value, disabled: !enabledValues.has(value) }))
    )
  }, [rows, rowsForServiceOptions])

  const accountOptions = useMemo(() => {
    const allValues = Array.from(
      new Map(
        rows.map((row) => [
          String(row.accountId),
          {
            label: getAccountDisplayLabel(row),
            searchText: `${getAccountDisplayLabel(row)} ${row.account_name || ''} ${row.accountId}`
          }
        ])
      ).entries()
    )
    const enabledValues = new Set(rowsForAccountOptions.map((row) => String(row.accountId)))

    return sortFilterOptions(
      allValues.map(([value, item]) => ({ value, label: item.label, searchText: item.searchText, disabled: !enabledValues.has(value) }))
    )
  }, [rows, rowsForAccountOptions])

  const campaignOptions = useMemo(() => {
    const allValues = Array.from(
      new Map(
        rows.map((row) => [
          getCampaignKey(row),
          {
            value: getCampaignKey(row),
            label: getCampaignDisplayLabel(row),
            searchText: `${getCampaignDisplayLabel(row)} ${row.campaignId ?? ''} ${getAccountDisplayLabel(row)} ${row.service}`
          }
        ])
      ).values()
    )
    const enabledValues = new Set(rowsForCampaignOptions.map((row) => getCampaignKey(row)))

    return sortFilterOptions(
      allValues.map((option) => ({
        ...option,
        disabled: !enabledValues.has(option.value)
      }))
    )
  }, [rows, rowsForCampaignOptions])

  useEffect(() => {
    setSelectedServices((current) => current === null ? null : current.filter((value) => serviceOptions.some((option) => option.value === value && !option.disabled)))
  }, [serviceOptions])

  useEffect(() => {
    setSelectedAccountIds((current) => current === null ? null : current.filter((value) => accountOptions.some((option) => option.value === value && !option.disabled)))
  }, [accountOptions])

  useEffect(() => {
    setSelectedCampaignKeys((current) => current === null ? null : current.filter((value) => campaignOptions.some((option) => option.value === value && !option.disabled)))
  }, [campaignOptions])

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (selectedServices !== null && selectedServices.length > 0 && !selectedServices.includes(row.service)) return false
      if (selectedAccountIds !== null && selectedAccountIds.length > 0 && !selectedAccountIds.includes(String(row.accountId))) return false
      if (selectedCampaignKeys !== null && selectedCampaignKeys.length > 0 && !selectedCampaignKeys.includes(getCampaignKey(row))) return false
      return true
    })
  }, [rows, selectedServices, selectedAccountIds, selectedCampaignKeys])

  const rowsForPlan = useMemo(() => {
    return rows.filter((row) => {
      if (selectedServices !== null && selectedServices.length > 0 && !selectedServices.includes(row.service)) return false
      if (selectedAccountIds !== null && selectedAccountIds.length > 0 && !selectedAccountIds.includes(String(row.accountId))) return false
      return true
    })
  }, [rows, selectedServices, selectedAccountIds])

  const planHint = selectedCampaignKeys !== null && selectedCampaignKeys.length > 0
    ? 'План не учитывает фильтр по кампаниям и показывается по таргетологу целиком.'
    : null

  const planWeeksInScope = useMemo(() => {
    if (!selectedRange) return [] as MetricWeek[]
    if (selectedRange.mode === 'week' && selectedRange.weekNumber) {
      return planWeeks.filter((week) => week.week_number_in_period === selectedRange.weekNumber)
    }
    return planWeeks
  }, [planWeeks, selectedRange])

  const planDraftsByPeriod = useMemo(() => {
    const result = new Map<number, Record<string, Record<number, { status?: 'planned' | 'unset' | 'excluded'; leads?: number | null; spend?: number | null }>>>()
    const weeksByPeriod = new Map<number, MetricWeek[]>()

    planWeeks.forEach((week) => {
      const current = weeksByPeriod.get(week.period_id) || []
      current.push(week)
      weeksByPeriod.set(week.period_id, current)
    })

    weeksByPeriod.forEach((periodWeeks, periodId) => {
      const weekNumberById = new Map(periodWeeks.map((week) => [week.week_id, week.week_number_in_period]))
      const draft: Record<string, Record<number, { status?: 'planned' | 'unset' | 'excluded'; leads?: number | null; spend?: number | null }>> = {}

      planRowsData
        .filter((row) => weekNumberById.has(row.week_id))
        .forEach((row) => {
          const weekNumber = weekNumberById.get(row.week_id)
          if (!weekNumber) return

          const targetKey = getPartnerMarketingTargetKey(row.service, row.account_id)
          const targetDraft = draft[targetKey] || {}
          targetDraft[weekNumber] = {
            status: row.planning_status,
            leads: row.leads_plan,
            spend: row.spend_plan
          }
          draft[targetKey] = targetDraft
        })

      result.set(periodId, draft)
    })

    return result
  }, [planRowsData, planWeeks])

  const planByTarget = useMemo(() => {
    if (!selectedRange) return new Map<string, { leads: number; spend: number; currency: string | null }>()

    const result = new Map<string, { leads: number; spend: number; currency: string | null }>()
    const targetMeta = new Map<string, string | null>()
    const weeksByPeriod = new Map<number, MetricWeek[]>()

    planWeeksInScope.forEach((week) => {
      const current = weeksByPeriod.get(week.period_id) || []
      current.push(week)
      weeksByPeriod.set(week.period_id, current)
    })

    rowsForPlan.forEach((row) => {
      const targetKey = getPartnerMarketingTargetKey(row.service, row.accountId)
      if (!targetMeta.has(targetKey)) {
        targetMeta.set(targetKey, row.accountCurrency || null)
      }
    })

    targetMeta.forEach((currency, targetKey) => {
      const aggregate = { leads: 0, spend: 0, currency }

      planWeeksInScope.forEach((week) => {
        const periodWeeks = weeksByPeriod.get(week.period_id) || []
        const periodDraft = planDraftsByPeriod.get(week.period_id) || {}
        const plan = getEffectiveWeeklyPlan(targetKey, week.week_number_in_period, periodDraft, periodWeeks)
        if (plan.status !== 'planned') return

        const overlapDays = getOverlapDays(selectedRange.start, selectedRange.end, week.week_start, week.week_end)
        if (overlapDays <= 0) return

        const weekBaseDays = Math.max(1, week.week_len_days || getInclusiveDays(week.week_start, week.week_end))
        const ratio = overlapDays / weekBaseDays

        aggregate.leads += (plan.leads || 0) * ratio
        aggregate.spend += (plan.spend || 0) * ratio
      })

      result.set(targetKey, aggregate)
    })

    return result
  }, [planDraftsByPeriod, planWeeksInScope, rowsForPlan, selectedRange])

  const unsetPlanSummary = useMemo(() => {
    const targetKeys = Array.from(
      new Set(rowsForPlan.map((row) => getPartnerMarketingTargetKey(row.service, row.accountId)))
    )

    if (targetKeys.length === 0 || planWeeksInScope.length === 0) {
      return { count: 0, label: null as string | null }
    }

    const count = targetKeys.reduce((acc, targetKey) => {
      const hasUnsetWeek = planWeeksInScope.some((week) => {
        if (!selectedRange) return false
        if (getOverlapDays(selectedRange.start, selectedRange.end, week.week_start, week.week_end) <= 0) return false
        const periodWeeks = planWeeksInScope.filter((item) => item.period_id === week.period_id)
        const periodDraft = planDraftsByPeriod.get(week.period_id) || {}
        const plan = getEffectiveWeeklyPlan(targetKey, week.week_number_in_period, periodDraft, periodWeeks)
        return plan.status === 'unset'
      })

      return acc + (hasUnsetWeek ? 1 : 0)
    }, 0)

    if (count === 0) {
      return { count: 0, label: null as string | null }
    }

    const label =
      selectedRange?.mode === 'week'
        ? `План не задан у ${count} ${pluralizeRu(count, 'таргетолога', 'таргетологов', 'таргетологов')} на выбранную неделю.`
        : `План не задан у ${count} ${pluralizeRu(count, 'таргетолога', 'таргетологов', 'таргетологов')} в выбранном периоде.`

    return { count, label }
  }, [planDraftsByPeriod, planWeeksInScope, rowsForPlan, selectedRange, selectedRange?.mode])

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        acc.spend += row.cost
        acc.conversions += row.conversions
        acc.accounts.add(row.accountId)
        const currencyKey = getAccountCurrency(row.accountCurrency)
        acc.spendByCurrency[currencyKey] = (acc.spendByCurrency[currencyKey] || 0) + row.cost
        return acc
      },
      {
        spend: 0,
        spendByCurrency: {} as Record<string, number>,
        conversions: 0,
        accounts: new Set<number>()
      }
    )
  }, [filteredRows])

  const selectedCurrency = getSingleCurrency(totals.spendByCurrency)
  const hasMixedCurrencies = getCurrencyCodes(totals.spendByCurrency).length > 1
  const averageCpa = selectedCurrency && totals.conversions > 0 ? totals.spend / totals.conversions : null
  const planTotals = useMemo(() => {
    let spend = 0
    let leads = 0
    const spendByCurrency: Record<string, number> = {}

    planByTarget.forEach((item) => {
      spend += item.spend
      leads += item.leads
      const currencyKey = getAccountCurrency(item.currency)
      spendByCurrency[currencyKey] = (spendByCurrency[currencyKey] || 0) + item.spend
    })

    return {
      spend,
      leads,
      spendByCurrency,
      cpa: leads > 0 ? spend / leads : null
    }
  }, [planByTarget])

  const dailyRows = useMemo<DailyChartRow[]>(() => {
    const map = new Map<string, DailyChartRow>()

    for (const row of filteredRows) {
      const current = map.get(row.date) || {
        date: row.date,
        spend: 0,
        conversions: 0,
        cpa: null,
        currency: getAccountCurrency(row.accountCurrency)
      }

      current.spend += row.cost
      current.conversions += row.conversions
      current.cpa = current.conversions > 0 && current.currency ? current.spend / current.conversions : null
      if (current.currency !== getAccountCurrency(row.accountCurrency)) {
        current.currency = null
      }
      map.set(row.date, current)
    }

    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date))
  }, [filteredRows])

  const displayDailyRows = useMemo<DailyChartRow[]>(() => {
    if (!includeVat) return dailyRows

    return dailyRows.map((row) => ({
      ...row,
      spend: getDisplayMoneyValue(row.spend, true),
      cpa: row.cpa === null ? null : getDisplayMoneyValue(row.cpa, true)
    }))
  }, [dailyRows, includeVat])

  const accountAnalysisRows = filteredRows

  const accountRangeContext = useMemo(() => {
    if (!selectedRange) return null
    const rangeStart = new Date(`${selectedRange.start}T00:00:00`)
    const rangeEnd = new Date(`${selectedRange.end}T00:00:00`)
    const yesterday = new Date()
    yesterday.setHours(0, 0, 0, 0)
    yesterday.setDate(yesterday.getDate() - 1)

    const effectiveEnd = rangeEnd < yesterday ? rangeEnd : yesterday
    if (effectiveEnd < rangeStart) {
      return {
        start: selectedRange.start,
        end: selectedRange.start,
        elapsedDays: 0,
        totalDays: Math.max(1, selectedRange.rangeDays || 1),
        hasStarted: false,
        isCompleted: false
      }
    }

    return {
      start: selectedRange.start,
      end: toIsoDate(effectiveEnd),
      elapsedDays: Math.floor((effectiveEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1,
      totalDays: Math.max(1, selectedRange.rangeDays || 1),
      hasStarted: true,
      isCompleted: rangeEnd <= yesterday
    }
  }, [selectedRange])

  const accountComparisonBaseRows = useMemo(() => {
    const rowsToRender = buildAccountComparisonRows(accountAnalysisRows)

    rowsToRender.sort((a, b) => {
      switch (accountTableSort) {
        case 'spend_asc':
          return a.spend - b.spend || b.conversions - a.conversions || a.key.localeCompare(b.key)
        case 'spend_desc':
          return b.spend - a.spend || b.conversions - a.conversions || a.key.localeCompare(b.key)
        case 'conversions_asc':
          return a.conversions - b.conversions || b.spend - a.spend || a.key.localeCompare(b.key)
        case 'conversions_desc':
          return b.conversions - a.conversions || b.spend - a.spend || a.key.localeCompare(b.key)
        case 'cpa_asc': {
          const aValid = a.spend > 0 && a.conversions > 0 && a.cpa !== null
          const bValid = b.spend > 0 && b.conversions > 0 && b.cpa !== null
          if (aValid !== bValid) return aValid ? -1 : 1
          if (!aValid && !bValid) return b.spend - a.spend || a.key.localeCompare(b.key)
          return (a.cpa || 0) - (b.cpa || 0) || b.conversions - a.conversions || b.spend - a.spend || a.key.localeCompare(b.key)
        }
        case 'cpa_desc': {
          const aValid = a.spend > 0 && a.conversions > 0 && a.cpa !== null
          const bValid = b.spend > 0 && b.conversions > 0 && b.cpa !== null
          if (aValid !== bValid) return aValid ? -1 : 1
          if (!aValid && !bValid) return b.spend - a.spend || a.key.localeCompare(b.key)
          return (b.cpa || 0) - (a.cpa || 0) || b.spend - a.spend || b.conversions - a.conversions || a.key.localeCompare(b.key)
        }
        case 'account_asc':
          return a.accountLabel.localeCompare(b.accountLabel, 'ru') || a.serviceLabel.localeCompare(b.serviceLabel, 'ru') || b.spend - a.spend
        case 'account_desc':
          return b.accountLabel.localeCompare(a.accountLabel, 'ru') || a.serviceLabel.localeCompare(b.serviceLabel, 'ru') || b.spend - a.spend
        case 'service_asc':
          return a.serviceLabel.localeCompare(b.serviceLabel, 'ru') || a.accountLabel.localeCompare(b.accountLabel, 'ru') || b.spend - a.spend
        case 'service_desc':
          return b.serviceLabel.localeCompare(a.serviceLabel, 'ru') || a.accountLabel.localeCompare(b.accountLabel, 'ru') || b.spend - a.spend
        case 'campaigns_asc':
          return a.campaignCount - b.campaignCount || b.spend - a.spend || a.key.localeCompare(b.key)
        case 'campaigns_desc':
          return b.campaignCount - a.campaignCount || b.spend - a.spend || a.key.localeCompare(b.key)
        default:
          return 0
      }
    })

    return rowsToRender
  }, [accountAnalysisRows, accountTableSort])

  const accountComparisonRows = useMemo(() => {
    if (!accountRangeContext) {
      return accountComparisonBaseRows
    }

    const weeksByPeriod = new Map<number, MetricWeek[]>()

    planWeeksInScope.forEach((week) => {
      const current = weeksByPeriod.get(week.period_id) || []
      current.push(week)
      weeksByPeriod.set(week.period_id, current)
    })

    return accountComparisonBaseRows.map((row) => {
      let planSpend = 0
      let planLeads = 0

      planWeeksInScope.forEach((week) => {
        const overlapDays = getOverlapDays(accountRangeContext.start, accountRangeContext.end, week.week_start, week.week_end)
        if (overlapDays <= 0) return

        const periodWeeks = weeksByPeriod.get(week.period_id) || []
        const periodDraft = planDraftsByPeriod.get(week.period_id) || {}
        const plan = getEffectiveWeeklyPlan(row.key, week.week_number_in_period, periodDraft, periodWeeks)
        if (plan.status !== 'planned') return

        const weekBaseDays = Math.max(1, week.week_len_days || getInclusiveDays(week.week_start, week.week_end))
        const ratio = overlapDays / weekBaseDays
        planSpend += (plan.spend || 0) * ratio
        planLeads += (plan.leads || 0) * ratio
      })

      const periodPlan = planByTarget.get(row.key)
      const periodPlanSpend = periodPlan?.spend || 0
      const periodPlanLeads = periodPlan?.leads || 0
      const periodPlanCpa = periodPlanLeads > 0 ? periodPlanSpend / periodPlanLeads : null
      const planCpa = periodPlanCpa
      const rowCurrency = getSingleCurrency(row.spendByCurrency)
      const spendDeviation = rowCurrency && planSpend > 0 ? row.spend - planSpend : null
      const spendDeviationPercent = spendDeviation !== null && planSpend > 0
        ? (spendDeviation / planSpend) * 100
        : null
      const leadsDeviation = planLeads > 0 ? row.conversions - planLeads : null
      const leadsDeviationPercent = leadsDeviation !== null && planLeads > 0
        ? (leadsDeviation / planLeads) * 100
        : null
      const cpaDeviation = row.cpa !== null && planCpa !== null ? row.cpa - planCpa : null
      const cpaDeviationPercent = cpaDeviation !== null && planCpa && planCpa > 0
        ? (cpaDeviation / planCpa) * 100
        : null
      const leadsForecast = accountRangeContext.hasStarted && accountRangeContext.elapsedDays > 0
        ? (row.conversions * accountRangeContext.totalDays) / accountRangeContext.elapsedDays
        : null
      const leadsForecastDeviationPercent = leadsForecast !== null && periodPlanLeads > 0
        ? ((leadsForecast - periodPlanLeads) / periodPlanLeads) * 100
        : null

      return {
        ...row,
        planSpend,
        planLeads,
        planCpa,
        periodPlanSpend,
        periodPlanLeads,
        periodPlanCpa,
        spendDeviation,
        spendDeviationPercent,
        leadsDeviation,
        leadsDeviationPercent,
        cpaDeviation,
        cpaDeviationPercent,
        leadsForecast,
        leadsForecastDeviationPercent
      }
    })
  }, [accountComparisonBaseRows, accountRangeContext, planByTarget, planDraftsByPeriod, planWeeksInScope])

  const vkAnalysisRows = useMemo(
    () => filteredRows.filter((row) => row.service === 'VK_ADS'),
    [filteredRows]
  )
  const factorAnalysisDate = useMemo(() => getLatestDateKey(vkAnalysisRows), [vkAnalysisRows])
  const factorAlerts = useMemo(() => {
    return buildMarketingFactorAlerts({
      rows: vkAnalysisRows,
      planTotals,
      includeVat,
      hasMixedCurrencies,
      selectedCurrency,
      getAccountCurrency,
      getAccountDisplayLabel,
      formatMoneyByCurrencies,
      formatMoneyValue,
      getSingleCurrency
    })
  }, [vkAnalysisRows, planTotals, includeVat, hasMixedCurrencies, selectedCurrency])

  const spendKpiValue = formatMoneyByCurrencies(totals.spendByCurrency, includeVat)
  const currencyBanner =
    hasMixedCurrencies
      ? 'Данные включают несколько валют. Чтобы видеть итоговые денежные показатели точнее, сузьте выборку по таргетологу.'
      : null
  const rangeProgress = useMemo(() => {
    if (!selectedRange) {
      return {
        elapsedDays: 0,
        totalDays: 0,
        hasStarted: false,
        isCompleted: false
      }
    }

    const rangeStart = new Date(`${selectedRange.start}T00:00:00`)
    const rangeEnd = new Date(`${selectedRange.end}T00:00:00`)
    const yesterday = new Date()
    yesterday.setHours(0, 0, 0, 0)
    yesterday.setDate(yesterday.getDate() - 1)

    const effectiveEnd = rangeEnd < yesterday ? rangeEnd : yesterday
    if (effectiveEnd < rangeStart) {
      return {
        elapsedDays: 0,
        totalDays: Math.max(1, selectedRange.rangeDays || 1),
        hasStarted: false,
        isCompleted: false
      }
    }

    return {
      elapsedDays: Math.floor((effectiveEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1,
      totalDays: Math.max(1, selectedRange.rangeDays || 1),
      hasStarted: true,
      isCompleted: rangeEnd <= yesterday
    }
  }, [selectedRange])

  const spendPlanTodayByCurrency = useMemo(() => {
    if (rangeProgress.totalDays <= 0 || rangeProgress.elapsedDays <= 0) return {} as Record<string, number>
    return scaleSpendByCurrencies(planTotals.spendByCurrency, rangeProgress.elapsedDays / rangeProgress.totalDays)
  }, [planTotals.spendByCurrency, rangeProgress.elapsedDays, rangeProgress.totalDays])

  const spendForecastByCurrency = useMemo(() => {
    if (rangeProgress.elapsedDays <= 0 || rangeProgress.totalDays <= 0) return {} as Record<string, number>
    return scaleSpendByCurrencies(totals.spendByCurrency, rangeProgress.totalDays / rangeProgress.elapsedDays)
  }, [totals.spendByCurrency, rangeProgress.elapsedDays, rangeProgress.totalDays])

  const leadsPlanToday = useMemo(() => {
    if (rangeProgress.totalDays <= 0 || rangeProgress.elapsedDays <= 0) return 0
    return (planTotals.leads * rangeProgress.elapsedDays) / rangeProgress.totalDays
  }, [planTotals.leads, rangeProgress.elapsedDays, rangeProgress.totalDays])

  const leadsForecast = useMemo(() => {
    if (rangeProgress.totalDays <= 0 || rangeProgress.elapsedDays <= 0) return null
    return (totals.conversions * rangeProgress.totalDays) / rangeProgress.elapsedDays
  }, [totals.conversions, rangeProgress.elapsedDays, rangeProgress.totalDays])

  const leadsRiskState = useMemo(() => {
    if (!rangeProgress.hasStarted) {
      return {
        label: 'Риск недобора',
        value: 'Диапазон не начался',
        hint: undefined,
        tone: 'default' as const
      }
    }

    if (planTotals.leads <= 0) {
      return {
        label: 'Риск недобора',
        value: 'План не задан',
        hint: leadsForecast !== null ? `Прогноз: ${formatNumber(leadsForecast)}` : undefined,
        tone: 'default' as const
      }
    }

    if (leadsForecast === null) {
      return {
        label: 'Риск недобора',
        value: 'Недостаточно факта',
        hint: undefined,
        tone: 'default' as const
      }
    }

    const gap = leadsForecast - planTotals.leads
    if (gap < 0) {
      return {
        label: 'Риск недобора',
        value: formatNumber(Math.abs(gap)),
        hint: `Прогноз: ${formatNumber(leadsForecast)} из ${formatNumber(planTotals.leads)}`,
        tone: 'negative' as const
      }
    }

    if (gap > 0) {
      return {
        label: 'Запас к плану',
        value: `+${formatNumber(gap)}`,
        hint: `Прогноз: ${formatNumber(leadsForecast)} из ${formatNumber(planTotals.leads)}`,
        tone: 'positive' as const
      }
    }

    return {
      label: 'Темп к плану',
      value: 'На плане',
      hint: `Прогноз: ${formatNumber(leadsForecast)} из ${formatNumber(planTotals.leads)}`,
      tone: 'default' as const
    }
  }, [leadsForecast, planTotals.leads, rangeProgress.hasStarted])

  const cplDeviationState = useMemo(() => {
    if (hasMixedCurrencies) {
      return {
        value: 'Уточните валюту',
        hint: 'Для CPL лучше смотреть выборку в одной валюте.',
        tone: 'warning' as const
      }
    }

    if (planTotals.cpa === null || planTotals.cpa <= 0) {
      return {
        value: 'План не задан',
        hint: undefined,
        tone: 'default' as const
      }
    }

    if (averageCpa === null) {
      return {
        value: 'Нет лидов для расчета',
        hint: undefined,
        tone: 'default' as const
      }
    }

    const deviationPct = ((averageCpa - planTotals.cpa) / planTotals.cpa) * 100
    if (Math.abs(deviationPct) < 0.1) {
      return {
        value: 'На плане',
        hint: undefined,
        tone: 'default' as const
      }
    }

    return {
      value: `${formatPercent(deviationPct)} к плану`,
      hint: undefined,
      tone: deviationPct > 0 ? ('negative' as const) : ('positive' as const)
    }
  }, [averageCpa, hasMixedCurrencies, includeVat, planTotals.cpa, selectedCurrency])

  const spendProgress = useMemo(() => {
    if (!rangeProgress.hasStarted) return null
    if (hasMixedCurrencies) return null
    if (planTotals.spend <= 0) return null
    return (totals.spend / planTotals.spend) * 100
  }, [hasMixedCurrencies, planTotals.spend, rangeProgress.hasStarted, totals.spend])

  const spendPlanToDatePercent = useMemo(() => {
    if (!rangeProgress.hasStarted) return null
    if (planTotals.spend <= 0) return null
    return (Object.values(spendPlanTodayByCurrency)[0] || 0) / planTotals.spend * 100
  }, [planTotals.spend, rangeProgress.hasStarted, spendPlanTodayByCurrency])

  const leadsProgress = useMemo(() => {
    if (!rangeProgress.hasStarted) return null
    if (planTotals.leads <= 0) return null
    return (totals.conversions / planTotals.leads) * 100
  }, [planTotals.leads, rangeProgress.hasStarted, totals.conversions])

  const leadsPlanToDatePercent = useMemo(() => {
    if (!rangeProgress.hasStarted || planTotals.leads <= 0) return null
    return (leadsPlanToday / planTotals.leads) * 100
  }, [leadsPlanToday, planTotals.leads, rangeProgress.hasStarted])

  const spendCornerTone = useMemo<'default' | 'positive' | 'negative'>(() => {
    if (spendProgress === null || spendPlanToDatePercent === null) return 'default'
    return Math.abs(spendProgress - spendPlanToDatePercent) <= 2 ? 'positive' : 'negative'
  }, [spendPlanToDatePercent, spendProgress])

  const leadsCornerTone = useMemo<'default' | 'positive' | 'negative'>(() => {
    if (leadsProgress === null || leadsPlanToDatePercent === null) return 'default'
    return leadsProgress >= leadsPlanToDatePercent ? 'positive' : 'negative'
  }, [leadsPlanToDatePercent, leadsProgress])

  const spendDeltaTile = useMemo(() => {
    if (!rangeProgress.hasStarted) {
      return {
        label: 'Отклонение',
        value: 'Диапазон не начался',
        tone: 'default' as const
      }
    }

    if (hasMixedCurrencies) {
      return {
        label: 'Отклонение',
        value: 'Уточните валюту',
        tone: 'warning' as const
      }
    }

    const forecastSpend = Object.values(spendForecastByCurrency)[0] || 0
    const diff = forecastSpend - planTotals.spend

    if (planTotals.spend <= 0) {
      return {
        label: 'Отклонение',
        value: 'План не задан',
        tone: 'default' as const
      }
    }

    if (Math.abs(diff) < 0.01) {
      return {
        label: 'В плане',
        value: formatMoneyValue(0, selectedCurrency, includeVat),
        tone: 'default' as const
      }
    }

    return {
      label: rangeProgress.isCompleted
        ? (diff > 0 ? 'Выше плана на' : 'Ниже плана на')
        : (diff > 0 ? 'Риск перерасхода' : 'Риск недобора'),
      value: formatMoneyValue(Math.abs(diff), selectedCurrency, includeVat),
      tone: 'negative' as const
    }
  }, [hasMixedCurrencies, includeVat, planTotals.spend, rangeProgress.hasStarted, rangeProgress.isCompleted, selectedCurrency, spendForecastByCurrency])

  const spendForecastTile = useMemo(() => {
    const forecastLabel = rangeProgress.isCompleted ? 'Факт периода' : 'Прогноз на период'

    if (!rangeProgress.hasStarted) {
      return {
        label: forecastLabel,
        value: 'Диапазон не начался',
        tone: 'default' as const
      }
    }

    if (Object.keys(spendForecastByCurrency).length === 0) {
      return {
        label: forecastLabel,
        value: 'Недостаточно факта',
        tone: 'default' as const
      }
    }

    return {
      label: forecastLabel,
      value: `${formatCompactMoneyByCurrencies(spendForecastByCurrency, includeVat)} из ${formatCompactMoneyByCurrencies(planTotals.spendByCurrency, includeVat)}`,
      tone: 'warning' as const
    }
  }, [includeVat, planTotals.spendByCurrency, rangeProgress.hasStarted, rangeProgress.isCompleted, spendForecastByCurrency])

  const leadsDeltaTile = useMemo(() => {
    if (!rangeProgress.hasStarted) {
      return {
        label: 'Риск недобора',
        value: 'Диапазон не начался',
        tone: 'default' as const
      }
    }

    if (planTotals.leads <= 0) {
      return {
        label: 'Риск недобора',
        value: 'План не задан',
        tone: 'default' as const
      }
    }

    if (leadsForecast === null) {
      return {
        label: 'Риск недобора',
        value: 'Недостаточно факта',
        tone: 'default' as const
      }
    }

    const gap = leadsForecast - planTotals.leads
    if (Math.abs(gap) < 0.01) {
      return {
        label: 'В плане',
        value: formatNumber(0),
        tone: 'default' as const
      }
    }

    return {
      label: gap < 0 ? 'Риск недобора' : 'Выше плана на',
      value: formatInteger(Math.abs(gap)),
      tone: gap < 0 ? ('negative' as const) : ('positive' as const)
    }
  }, [leadsForecast, planTotals.leads, rangeProgress.hasStarted])

  const leadsForecastTile = useMemo(() => {
    const forecastLabel = rangeProgress.isCompleted ? 'Факт периода' : 'Прогноз на период'

    if (!rangeProgress.hasStarted) {
      return {
        label: forecastLabel,
        value: 'Диапазон не начался',
        tone: 'default' as const
      }
    }

    if (leadsForecast === null) {
      return {
        label: forecastLabel,
        value: 'Недостаточно факта',
        tone: 'default' as const
      }
    }

    return {
      label: forecastLabel,
      value: `${formatInteger(leadsForecast)} из ${formatInteger(planTotals.leads)}`,
      tone: 'warning' as const
    }
  }, [leadsForecast, planTotals.leads, rangeProgress.hasStarted, rangeProgress.isCompleted])

  const spendPlanLabel = rangeProgress.isCompleted
    ? `План периода (${formatPercentPlain(100)})`
    : spendPlanToDatePercent !== null
      ? `План на вчера (${formatPercentPlain(spendPlanToDatePercent)})`
      : 'План на вчера'

  const leadsPlanLabel = rangeProgress.isCompleted
    ? `План периода (${formatPercentPlain(100)})`
    : leadsPlanToDatePercent !== null
      ? `План на вчера (${formatPercentPlain(leadsPlanToDatePercent)})`
      : 'План на вчера'

  const shouldReserveCplProgressSpace = spendProgress !== null || leadsProgress !== null

  const cplDeltaTile = useMemo(() => {
    if (hasMixedCurrencies) {
      return {
        label: 'Сравнение с планом',
        value: 'Уточните валюту',
        tone: 'warning' as const
      }
    }

    if (averageCpa === null || planTotals.cpa === null || planTotals.cpa <= 0) {
      return {
        label: 'Сравнение с планом',
        value: 'План не задан',
        tone: 'default' as const
      }
    }

    const diff = averageCpa - planTotals.cpa
    if (Math.abs(diff) < 0.01) {
      return {
        label: 'Сравнение с планом',
        value: formatMoneyValue(0, selectedCurrency, includeVat),
        tone: 'default' as const
      }
    }

    return {
      label: diff > 0 ? 'Выше плана на' : 'Ниже плана на',
      value: formatMoneyValue(Math.abs(diff), selectedCurrency, includeVat),
      tone: diff > 0 ? ('negative' as const) : ('positive' as const)
    }
  }, [averageCpa, hasMixedCurrencies, includeVat, planTotals.cpa, selectedCurrency])

  if (periods.loading || loading) {
    return <LoadingScreen />
  }

  if (parsedStudioId && Number.isFinite(parsedStudioId)) {
    return <Navigate to={`/partners/${parsedPartnerNumber}/marketing`} replace />
  }

  return (
    <div className="space-y-4">
      <Card className={`${SURFACE_CARD_CLASS} overflow-hidden p-0`}>
        <div className="space-y-3 px-5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-lg font-semibold">Обзор маркетинга</h1>
                  <button
                    type="button"
                    onClick={() => setMarketingInfoOpen(true)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-amber-300 bg-amber-100 px-3 py-1.5 text-sm font-semibold text-amber-900 shadow-sm transition hover:bg-amber-200 hover:shadow"
                    aria-label="Открыть важную информацию о названиях кабинетов и обновлении данных"
                    title="Важно"
                  >
                    <Info className="h-4 w-4" />
                    Важно
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Факт по расходу, лидам и таргетологам из рекламных кабинетов Click.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/partners/${parsedPartnerNumber}/marketing/daily`}
                className="inline-flex items-center rounded-full border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground transition hover:bg-muted"
              >
                Сводка по дням
              </Link>
              <Link
                to={`/partners/${parsedPartnerNumber}/marketing/plan`}
                className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-4 py-2 text-sm font-medium text-amber-800 transition hover:bg-amber-200"
              >
                План по таргетологам
              </Link>
            </div>
          </div>

          {unsetPlanSummary.label ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5">
              <div className="flex min-w-0 items-center gap-2.5 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                <span className="font-medium">{unsetPlanSummary.label}</span>
              </div>
              <Link
                to={`/partners/${parsedPartnerNumber}/marketing/plan`}
                className="inline-flex shrink-0 items-center rounded-full border border-amber-200 bg-card px-3 py-1.5 text-sm font-medium text-amber-800 transition hover:bg-amber-100"
              >
                Открыть план
              </Link>
            </div>
          ) : null}

          <div className={`${SUBTLE_PANEL_CLASS} px-3 py-2 sm:px-4 sm:py-2.5`}>
            <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
              <div className="min-w-0 flex-1">
                <PeriodSelector
                  currentPeriod={periods.currentPeriod}
                  onPrevPeriod={periods.goToPrevPeriod}
                  onNextPeriod={periods.goToNextPeriod}
                  canGoPrev={periods.canGoPrev}
                  canGoNext={periods.canGoNext}
                  showViewModeSelector
                  isCurrentPeriod={periods.isCurrentPeriod}
                  periodFilterValue={selectedTimeFilter}
                  periodFilterOptions={timeFilterOptions}
                  onPeriodFilterChange={setSelectedTimeFilter}
                  selectedRange={selectedRange}
                  compact
                />
              </div>

              <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border/60 pt-2 xl:border-l xl:border-t-0 xl:pl-3 xl:pt-0">
                <span className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                  НДС
                </span>
                <div className="inline-flex rounded-full border border-border bg-muted p-0.5">
                  <button
                    type="button"
                    onClick={() => setIncludeVat(false)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                      !includeVat
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Без НДС
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeVat(true)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                      includeVat
                        ? 'bg-card text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    С НДС +22%
                  </button>
                </div>
              </div>
            </div>
          </div>

          {currencyBanner ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {currencyBanner}
            </div>
          ) : null}
        </div>
      </Card>

      {marketingInfoOpen ? <MarketingInfoModal onClose={() => setMarketingInfoOpen(false)} /> : null}

      {error && (
        <Card className="border border-red-300">
          <p className="text-sm text-red-600">{error}</p>
        </Card>
      )}

      <div className="grid gap-4 xl:grid-cols-3">
        <div>
          <KpiCard
            label="Расход"
            comparisonLabel="Факт / План периода"
            value={spendKpiValue}
            secondaryValue={formatMoneyByCurrencies(planTotals.spendByCurrency, includeVat)}
            cornerValue={spendProgress !== null ? formatPercentPlain(spendProgress) : null}
            cornerTone={spendCornerTone}
            progressValue={spendProgress !== null ? String(spendProgress) : null}
            progressMarkerValue={spendPlanToDatePercent !== null ? String(spendPlanToDatePercent) : null}
            progressTone="warning"
            planToDateLabel={spendPlanLabel}
            planToDateValue={rangeProgress.hasStarted ? formatMoneyByCurrencies(spendPlanTodayByCurrency, includeVat) : '—'}
            footerTiles={[spendForecastTile, spendDeltaTile]}
          />
        </div>
        <div>
          <KpiCard
            label="Лиды"
            comparisonLabel="Факт / План периода"
            value={formatNumber(totals.conversions)}
            secondaryValue={formatNumber(planTotals.leads)}
            cornerValue={leadsProgress !== null ? formatPercentPlain(leadsProgress) : null}
            cornerTone={leadsCornerTone}
            progressValue={leadsProgress !== null ? String(leadsProgress) : null}
            progressMarkerValue={leadsPlanToDatePercent !== null ? String(leadsPlanToDatePercent) : null}
            progressTone={leadsRiskState.tone}
            planToDateLabel={leadsPlanLabel}
            planToDateValue={rangeProgress.hasStarted ? formatNumber(leadsPlanToday) : '—'}
            footerTiles={[leadsForecastTile, leadsDeltaTile]}
          />
        </div>
        <div>
          <KpiCard
            label="CPL"
            comparisonLabel="Факт / План периода"
            value={formatCpa(averageCpa, selectedCurrency, includeVat)}
            secondaryValue={formatCpa(planTotals.cpa, selectedCurrency, includeVat)}
            reserveProgressSpace={shouldReserveCplProgressSpace}
            footerTiles={[
              cplDeltaTile,
              {
                label: 'Отклонение',
                value: cplDeviationState.value,
                hint: cplDeviationState.hint,
                tone: cplDeviationState.tone
              }
            ]}
          />
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-12">
        <div className="space-y-4 xl:col-span-8">
          <AccountComparisonTable
            rows={accountComparisonRows}
            sort={accountTableSort}
            includeVat={includeVat}
            onSortChange={setAccountTableSort}
            planHint={planHint}
            partnerNumber={parsedPartnerNumber}
            periodFilterValue={selectedTimeFilter}
          />

          <Card className={SURFACE_CARD_CLASS}>
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold">Динамика по дням</h2>
              </div>
              <div className="h-64">
                {dailyRows.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    За выбранный период пока нет данных.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={displayDailyRows} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
                      <CartesianGrid {...chartTheme.grid} vertical={false} />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} {...chartTheme.axis} />
                      <YAxis yAxisId="left" axisLine={false} tickLine={false} {...chartTheme.axis} />
                      <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} {...chartTheme.axis} />
                      <Tooltip content={<MarketingTooltip includeVat={includeVat} />} />
                      {!hasMixedCurrencies && (
                        <>
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="spend"
                            name="Расход"
                            stroke={chartSeries('amber', 500, '#f59e0b')}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4 }}
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="cpa"
                            name="CPL"
                            stroke={chartSeries('violet', 500, '#8b5cf6')}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4 }}
                            strokeDasharray="3 3"
                          />
                        </>
                      )}
                      <Line
                        yAxisId={hasMixedCurrencies ? 'left' : 'right'}
                        type="monotone"
                        dataKey="conversions"
                        name="Лиды"
                        stroke={chartSeries('slate', 500, '#64748b')}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4 }}
                        strokeDasharray="5 5"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </Card>
        </div>

        <div className="xl:col-span-4">
          <MarketingFactorAnalysisCard alerts={factorAlerts} analysisDate={factorAnalysisDate} />
        </div>
      </div>
    </div>
  )
}
